import { ORDER_SUCCESS_MOTION_DEFAULTS } from '~~/layers/base/constants/orderSuccessMotionDefaults'

export default defineAppConfig({
    ui: {
        /**
         * Map Nuxt UI Pro semantic color roles to the Crane DS palettes registered
         * in `bridge.css` `@theme inline`. Brand roles + status messaging ramps
         * (info→primary, success→tertiary, warning/error ladders in bridge).
         * `tertiary` is not a `ui.colors` entry; `--ui-tertiary` is repointed in
         * `bridge.css` `:root` for direct var() usage.
         */
        colors: {
            primary: 'primary',
            secondary: 'secondary',
            neutral: 'neutral',
            info: 'info',
            success: 'success',
            warning: 'warning',
            error: 'error',
        },
        font: {
            family: {
                sans: ['Montserrat', 'Helvetica', 'Arial', 'sans-serif'],
                serif: ['Playfair Display', 'Georgia', 'serif'],
                mono: [
                    'JetBrains Mono',
                    'ui-monospace',
                    'SFMono-Regular',
                    'Menlo',
                    'Monaco',
                    'Consolas',
                    'Liberation Mono',
                    'Courier New',
                    'monospace',
                ],
            },
            /**
             * Type sizes: DS responsive `--text-*` in `bridge.css` `@theme inline` +
             * `colors_and_type.css`. Do not duplicate fixed rem steps here (they override
             * the bridged scale and block breakpoint repointing).
             */
            weight: {
                '1': '500',
                thin: '100',
                extralight: '200',
                light: '300',
                normal: '400',
                medium: '500',
                semibold: '600',
                bold: '700',
                extrabold: '800',
                black: '900',
            },
        },
        shadows: {
            'drop-shadow-sm': {
                shadowType: 'dropShadow',
                radius: 2,
                color: '#0000000d',
                offsetX: 0,
                offsetY: 1,
                spread: 0,
            },
            'drop-shadow': {
                '0': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 2,
                        color: '#0000000f',
                        offsetX: 0,
                        offsetY: 1,
                        spread: 0,
                    },
                },
                '1': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 3,
                        color: '#0000001a',
                        offsetX: 0,
                        offsetY: 1,
                        spread: 0,
                    },
                },
                description: null,
            },
            'drop-shadow-md': {
                '0': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 4,
                        color: '#0000000f',
                        offsetX: 0,
                        offsetY: 2,
                        spread: -1,
                    },
                },
                '1': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 6,
                        color: '#0000001a',
                        offsetX: 0,
                        offsetY: 4,
                        spread: -1,
                    },
                },
                description: null,
            },
            'drop-shadow-lg': {
                '0': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 6,
                        color: '#0000000d',
                        offsetX: 0,
                        offsetY: 4,
                        spread: -2,
                    },
                },
                '1': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 15,
                        color: '#0000001a',
                        offsetX: 0,
                        offsetY: 10,
                        spread: -3,
                    },
                },
                description: null,
            },
            'drop-shadow-xl': {
                '0': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 10,
                        color: '#0000000a',
                        offsetX: 0,
                        offsetY: 10,
                        spread: -5,
                    },
                },
                '1': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 25,
                        color: '#0000001a',
                        offsetX: 0,
                        offsetY: 20,
                        spread: -5,
                    },
                },
                description: null,
            },
            'drop-shadow-2xl': {
                shadowType: 'dropShadow',
                radius: 50,
                color: '#00000040',
                offsetX: 0,
                offsetY: 25,
                spread: -12,
            },
            'focus-neutral': {
                '0': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#0c0a09ff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 4,
                    },
                },
                '1': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#ffffffff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 2,
                    },
                },
                description: null,
            },
            'focus-primary': {
                '0': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#0F172Bff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 4,
                    },
                },
                '1': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#ffffffff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 2,
                    },
                },
                description: null,
            },
            'focus-secondary': {
                '0': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#441320ff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 4,
                    },
                },
                '1': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#ffffffff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 2,
                    },
                },
                description: null,
            },
            'focus-info': {
                '0': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#0F172Bff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 4,
                    },
                },
                '1': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#ffffffff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 2,
                    },
                },
                description: null,
            },
            'focus-warning': {
                '0': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#d08700ff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 4,
                    },
                },
                '1': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#ffffffff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 2,
                    },
                },
                description: null,
            },
            'focus-success': {
                '0': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#0e492eff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 4,
                    },
                },
                '1': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#ffffffff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 2,
                    },
                },
                description: null,
            },
            'focus-error': {
                '0': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#c10007ff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 4,
                    },
                },
                '1': {
                    type: 'custom-shadow',
                    value: {
                        shadowType: 'dropShadow',
                        radius: 0,
                        color: '#ffffffff',
                        offsetX: 0,
                        offsetY: 0,
                        spread: 2,
                    },
                },
                description: null,
            },
        },
        spacing: {
            '1': '0.25rem',
            '2': '0.5rem',
            '3': '0.75rem',
            '4': '1rem',
            '5': '1.25rem',
            '6': '1.5rem',
            '7': '1.75rem',
            '8': '2rem',
            '9': '2.25rem',
            '10': '2.5rem',
            '11': '2.75rem',
            '12': '3rem',
            '14': '3.5rem',
            '16': '4rem',
            '20': '5rem',
            '24': '6rem',
            '28': '7rem',
            '32': '8rem',
            '36': '9rem',
            '40': '10rem',
            '44': '11rem',
            '48': '12rem',
            '52': '13rem',
            '56': '14rem',
            '60': '15rem',
            '64': '16rem',
            '72': '18rem',
            '80': '20rem',
            '96': '24rem',
            '1.5': '0.375rem',
            '2.5': '0.625rem',
            '0.5': '0.125rem',
            '3.5': '0.875rem',
        },
        /**
         * Radius ladder (Phase 5): mirrors bridge.css `@theme` + `:root` steps.
         *
         * REGULAR USE: `rounded` (4px) on cards/buttons/panels; `rounded-sm` (2px) only
         * for thumbnails/previews ≤120px; `rounded-full` for pills/avatars (R5).
         *
         * md → 4xl values are NOT for new leaf surfaces. They cover unmigrated legacy
         * classes and the DS nesting exception (R6): outer containers that wrap 4px
         * children. See bridge.css radius comments and migration/RULINGS.md #6.
         */
        rounded: {
            'rounded-xs': '0.125rem',
            'rounded-sm': '0.25rem',
            'rounded-md': '0.375rem',
            'rounded-lg': '0.5rem',
            'rounded-xl': '0.625rem',
            'rounded-2xl': '0.75rem',
            'rounded-3xl': '0.875rem',
            'rounded-full': '62.4375rem',
            'rounded-4xl': '1rem',
        },
        tailwindMerge: {
            extend: {
                theme: {
                    spacing: [
                        '0.5',
                        '1',
                        '1.5',
                        '2',
                        '2.5',
                        '3',
                        '3.5',
                        '4',
                        '5',
                        '6',
                        '7',
                        '8',
                        '9',
                        '10',
                        '11',
                        '12',
                        '14',
                        '16',
                        '20',
                        '24',
                        '28',
                        '32',
                        '36',
                        '40',
                        '44',
                        '48',
                        '52',
                        '56',
                        '60',
                        '64',
                        '72',
                        '80',
                        '96',
                    ],
                },
            },
        },
        container: {
            base: 'w-full max-w-(--ui-container-mobile) lg:max-w-(--ui-container-desktop) px-6 sm:px-6 md:px-6 lg:px-6',
        },
        /** Default `USkeleton`: softer pulse floor (0.72 vs Tailwind pulse 0.5); see `main.css` */
        skeleton: {
            base: 'animate-pulse-skeleton rounded bg-elevated',
        },
        /**
         * Nuxt `color` only tints the toast icon and progress bar (`bg-default` body).
         * Crane `.hm-alert--*` paints the surface so error/success/warning read without an icon.
         * `close` matches title/icon — default neutral link reads as faint white on tinted surfaces.
         */
        toast: {
            variants: {
                color: {
                    error: {
                        root: 'bg-(--error-50) ring-(--error-700)/30',
                        title: 'text-(--error-700)',
                        icon: 'text-(--error-700)',
                        close: 'text-(--error-700) [&_.iconify]:text-(--error-700)',
                    },
                    success: {
                        root: 'bg-(--success-50) ring-(--success-700)/30',
                        title: 'text-(--success-700)',
                        icon: 'text-(--success-700)',
                        close: 'text-(--success-700) [&_.iconify]:text-(--success-700)',
                    },
                    warning: {
                        root: 'bg-(--warning-50) ring-(--warning-700)/30',
                        title: 'text-(--warning-700)',
                        icon: 'text-(--warning-700)',
                        close: 'text-(--warning-700) [&_.iconify]:text-(--warning-700)',
                    },
                },
            },
        },
        button: {
            slots: {
                leadingIcon: 'text-white w-5 h-5',
            },
        },
        input: {
            variants: {
                size: {
                    md: {
                        base: 'text-lg',
                    },
                },
            },
        },
        textarea: {
            variants: {
                size: {
                    md: {
                        base: 'text-lg',
                    },
                },
            },
        },
    },
    printingProcess: {
        startingFromText: 'from',
    },
    screenLock: {
        defaultTimeoutMs: 60000,
    },
    loadingScrim: {
        defaultTimeoutMs: 60000,
        /** Minimum Processing scrim visibility during handwritten-note Send (personalize → success). */
        handwritingSubmitMinDurationMs: 2000,
    },
    /**
     * Motion timings for handwritten-note / order success and status screens.
     * Consumed via {@link useOrderSuccessMotionConfig}; override individual keys here.
     */
    orderSuccess: ORDER_SUCCESS_MOTION_DEFAULTS,
    /**
     * Default `UIcon` / Nuxt Icon mode. `css` uses mask sprites (no real `<path>` nodes in the DOM);
     * `svg` renders inline SVG so Lucide strokes respond to `--icon-stroke-width` and normal SVG/CSS.
     * @see `doc/guide/GUIDE_icons.md`
     */
    icon: {
        mode: 'svg',
    },
})
